import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, forkJoin, merge, Observable, of } from 'rxjs';
import { ActionsEnum as AppActionsEnum } from '../../store/app.actions';
import { AUTH_KEYS } from '../auth/auth-keys.constant';
import { BrowserManagerDataDispatcher } from '../browser-manager-data/browser-manager-data.dispatcher';
import { BrowserManagerDataStore } from '../browser-manager-data/browser-manager-data.store';
import { CookieService } from '../browser-manager-data/cookie.service';
import { WindowRefService } from 'i2g-ng-utils';
import { NavigationService } from '../navigation/navigation.service';
import { UrlService } from '../shared/services/url/url.service';
import {
  ActionsEnum,
  authenticated,
  Authenticated,
  AuthenticatedThroughBilling,
  authenticatedThroughBilling,
  Login,
  LoginError,
  loginError,
  LoginSuccess,
  loginSuccess,
  logout,
  Logout,
  SetRedirectPath,
  setRedirectPath,
  unauthenticated,
  Unauthenticated,
  UpdateAccountId,
  updateAccountId,
  UpdateTokens,
  UpdateUserId,
  updateUserId,
} from './auth-data.actions';
import { AuthDataDispatcher } from './auth-data.dispatcher';
import { AuthDataService } from './auth-data.service';
import { AuthDataStore } from './auth-data.store';
import { catchError, flatMap, map, switchMap, take, tap } from 'rxjs/operators';
import { PARAMS } from '../billing-data/billing-data.data';
import { BrowserManagerDataService } from '../browser-manager-data/browser-manager-data.service';
import { environment } from '../../../environments/environment';
import { EnvironmentsEnum } from '../../../environments/environments.enum';

@Injectable()
export class AuthDataEffects {
  constructor(
    private _actions: Actions,
    private _authDataService: AuthDataService,
    private _authDataStore: AuthDataStore,
    private _authDataDispatcher: AuthDataDispatcher,
    private _navigationService: NavigationService,
    private _windowRefService: WindowRefService,
    private _urlService: UrlService,
    private _cookieService: CookieService,
    private _browserManagerDataStore: BrowserManagerDataStore,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _browserManagerDataService: BrowserManagerDataService
  ) {}

  @Effect()
  onAppInit: Observable<
    Authenticated | AuthenticatedThroughBilling | Logout
  > = merge(
    this._actions.pipe(
      ofType(AppActionsEnum.INIT_APP),
      tap(() =>
        this._authDataDispatcher.updateTokens(
          this._cookieService.getCookie(AUTH_KEYS.ACCESS_TOKEN),
          this._cookieService.getCookie(AUTH_KEYS.REFRESH_TOKEN)
        )
      )
    ),
    this._actions.pipe(ofType(ActionsEnum.LOGIN_SUCCESS))
  ).pipe(
    flatMap(() => this._authDataStore.getTokensLoaded()),
    map((tokensLoaded: boolean) => {
      const queryParams = this._navigationService.getLocationQueryParams();

      if (!!queryParams[PARAMS.ENCRYPTED_ACCOUNT_ID]) {
        // force user to sign back in after trying to access atlas through unauthenticated billing flow
        this._authDataDispatcher.updateTokens('', '');

        return authenticatedThroughBilling();
      } else if (tokensLoaded) {
        return authenticated();
      }

      return logout(true, this._navigationService.getCurrentUrl());
    })
  );

  @Effect()
  onAuthenticationThroughBilling: Observable<
    UpdateAccountId | UpdateUserId
  > = this._actions.pipe(
    ofType(ActionsEnum.AUTHENTICATED_THROUGH_BILLING),
    take(1),
    flatMap(() => concat([updateAccountId(null), updateUserId(null)]))
  );

  @Effect({ dispatch: false })
  updateTokens: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_TOKENS),
    tap(({ payload }: UpdateTokens) => {
      const { accessToken, refreshToken } = payload;

      this._browserManagerDataDispatcher.setCookies([
        {
          key: AUTH_KEYS.ACCESS_TOKEN,
          value: accessToken || '',
        },
        {
          key: AUTH_KEYS.REFRESH_TOKEN,
          value: refreshToken || '',
        },
      ]);
    })
  );

  @Effect({ dispatch: false })
  onUnAuth: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.UNAUTHENTICATED),
    switchMap(({ payload }: Unauthenticated) =>
      forkJoin(
        this._browserManagerDataStore.getDeviceId(),
        this._authDataStore.getRedirectPath()
      ).pipe(
        tap(([deviceId, redirectPath]: string[]) => {
          setTimeout(() => this._goToLoginPage(redirectPath));
        })
      )
    )
  );

  @Effect()
  login: Observable<LoginSuccess | LoginError> = this._actions.pipe(
    ofType(ActionsEnum.LOGIN),
    switchMap(({ payload }: Login) => {
      const { username, pw, accountId, dispatchId } = payload;

      return this._authDataService.login(username, pw, accountId).pipe(
        map(() => loginSuccess(dispatchId)),
        catchError((error) => of(loginError(dispatchId, error)))
      );
    })
  );

  @Effect()
  logout: Observable<SetRedirectPath | Unauthenticated> = this._actions.pipe(
    ofType(ActionsEnum.LOGOUT),
    flatMap(({ payload }: Logout) => {
      const { isForced, redirectPath } = payload;

      return forkJoin(
        this._authDataStore.getUserId(),
        this._authDataStore.getAccountId()
      ).pipe(
        flatMap(([userId, accountId]: string[]) => {
          this._browserManagerDataDispatcher.removeCookies([
            AUTH_KEYS.ACCESS_TOKEN,
            AUTH_KEYS.REFRESH_TOKEN,
          ]);
          this._browserManagerDataDispatcher.removeLocalStorageItem(
            `${accountId}_payment_method`
          );

          return concat([
            setRedirectPath(
              isForced
                ? redirectPath || this._navigationService.getCurrentUrl()
                : null
            ),
            unauthenticated(userId, accountId, isForced),
          ]);
        })
      );
    })
  );

  private _goToLoginPage(redirectPath: string) {
    const accountServerUrl = environment.accountServerUrl;
    let loginUrl = accountServerUrl;

    if (this._navigationService.getLocationHref() !== loginUrl) {
      if (redirectPath && environment.env !== EnvironmentsEnum.localdev) {
        const protocol = this._windowRefService.nativeWindow.location.protocol;
        const host = this._windowRefService.nativeWindow.location.host;
        const baseReturnUrl = [protocol, host].join('//');
        const redirectUrlQueryParam = this._urlService.serializeParams({
          returnUrl: [baseReturnUrl, redirectPath].join(''),
        });

        loginUrl = `${accountServerUrl}${Location.normalizeQueryParams(
          redirectUrlQueryParam
        )}`;
      }

      if (this._browserManagerDataService.isMobile()) {
        loginUrl = accountServerUrl;
      }

      this._navigationService.goToExternalUrl(loginUrl);
    }
  }
}
